import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../../hooks/useAuth";
import { useLocalization } from "../../hooks/useLocalization";
import { employeeApi } from "../../services/employeeApi";
import { medicalApi } from "../../services/medicalApi";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { EmployeeAvatar } from "../../components/EmployeeAvatar";
import { formatApiErrorDetail } from "../../lib/api";

export default function MyProfilePage() {
  const { user } = useAuth();
  const { t } = useLocalization();
  const [employee, setEmployee] = useState(null);
  const [medicalCheck, setMedicalCheck] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");

  useEffect(() => {
    if (user?.employee_id) employeeApi.get(user.employee_id).then((r) => setEmployee(r.data));
    medicalApi.myStatus().then((r) => setMedicalCheck(r.data)).catch(() => setMedicalCheck(null));
  }, [user]);

  const scheduleAppointment = async () => {
    if (!scheduleDate) { toast.error("Please pick a date"); return; }
    try {
      const { data } = await medicalApi.scheduleMyAppointment(scheduleDate);
      setMedicalCheck(data);
      setScheduleDate("");
      toast.success("Appointment date saved - reminders paused until then");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!employee) return <p className="text-sm text-gray-500">{t("common.loading")}</p>;

  return (
    <div data-testid="my-profile-page" className="space-y-5">
      <PageHelpBanner pageKey="my-profile" title="How to use this page">
        <p>Your basic details are shown read-only here - if a medical exam is due, enter your booked appointment date to pause reminders until then.</p>
      </PageHelpBanner>
      <h2 className="text-xl font-bold tracking-tight text-gray-900">{t("nav.myProfile")}</h2>
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-center gap-4 mb-4">
          <EmployeeAvatar employee={employee} size={56} />
          <div>
            <p className="font-semibold text-gray-900">{employee.legal_name}</p>
            <p className="text-xs text-gray-500">{employee.position}</p>
          </div>
        </div>
        <dl className="text-sm space-y-3">
          <div className="flex justify-between"><dt className="text-gray-500">{t("employees.employeeId")}</dt><dd className="font-semibold" data-testid="profile-employee-id">{employee.employee_id}</dd></div>
          <div className="flex justify-between"><dt className="text-gray-500">{t("employees.legalName")}</dt><dd>{employee.legal_name}</dd></div>
          <div className="flex justify-between"><dt className="text-gray-500">{t("employees.position")}</dt><dd>{employee.position}</dd></div>
          <div className="flex justify-between"><dt className="text-gray-500">{t("employees.storeLocation")}</dt><dd>{employee.store_location}</dd></div>
          <div className="flex justify-between"><dt className="text-gray-500">{t("employees.personalEmail")}</dt><dd>{employee.personal_email}</dd></div>
        </dl>
      </div>

      {medicalCheck && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3" data-testid="medical-check-card">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Medical Check</p>
            <StatusBadge status={medicalCheck.status} />
          </div>
          <dl className="text-sm space-y-1">
            <div className="flex justify-between"><dt className="text-gray-500">Last completed</dt><dd>{medicalCheck.last_completed_date || "-"}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Next due</dt><dd>{medicalCheck.next_due_date || "Not yet assigned"}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Scheduled appointment</dt><dd>{medicalCheck.scheduled_appointment_date || "None"}</dd></div>
          </dl>
          <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
            <input data-testid="medical-schedule-date-input" type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm flex-1" />
            <button data-testid="medical-schedule-save-btn" onClick={scheduleAppointment} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">Save Appointment</button>
          </div>
          <p className="text-xs text-gray-500">Enter the date of your booked appointment to pause reminders. It clears automatically once the date passes.</p>
        </div>
      )}
    </div>
  );
}
